import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ws, api } from '../lib/api';
import { topics } from '@sahaj/websocket';
import { useGroupWebRTC } from '../hooks/useGroupWebRTC';
import { ringtonePlayer } from '../utils/ringtone';
import type { GroupCallRoomDto, GroupParticipantDto, GroupCallSignalMessage, CallType } from '@sahaj/types';

export interface IncomingGroupCall {
  room: GroupCallRoomDto;
  callerName: string;
  channelName: string;
  callType: CallType;
}

export type GroupCallState = 'IDLE' | 'STARTING' | 'CONNECTED' | 'ERROR';

interface GroupCallContextType {
  activeGroupRoom: GroupCallRoomDto | null;
  incomingGroupCall: IncomingGroupCall | null;
  participants: GroupParticipantDto[];
  localStream: MediaStream | null;
  remoteStreams: Map<string, MediaStream>;
  screenStream: MediaStream | null;
  isJoined: boolean;
  isMuted: boolean;
  isCameraOff: boolean;
  isScreenSharing: boolean;
  callState: GroupCallState;
  roomFullError: string | null;
  initiateGroupCall: (params: { workspaceId: string; channelId: string; callType: CallType }) => Promise<void>;
  joinGroupCall: (params: { workspaceId: string; channelId: string; callType: CallType }) => Promise<void>;
  acceptIncomingGroupCall: () => Promise<void>;
  declineGroupCall: () => void;
  leaveGroupCall: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => void;
  clearRoomFullError: () => void;
  fetchActiveChannelCall: (workspaceId: string, channelId: string) => Promise<GroupCallRoomDto | null>;
}

const GroupCallContext = createContext<GroupCallContextType | undefined>(undefined);

export const GroupCallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [activeGroupRoom, setActiveGroupRoom] = useState<GroupCallRoomDto | null>(null);
  const [incomingGroupCall, setIncomingGroupCall] = useState<IncomingGroupCall | null>(null);
  const [participants, setParticipants] = useState<GroupParticipantDto[]>([]);
  const [isJoined, setIsJoined] = useState(false);
  const [callState, setCallState] = useState<GroupCallState>('IDLE');
  const [roomFullError, setRoomFullError] = useState<string | null>(null);
  const [iceServers, setIceServers] = useState<RTCIceServer[]>([]);

  const activeGroupRoomRef = useRef<GroupCallRoomDto | null>(null);
  activeGroupRoomRef.current = activeGroupRoom;

  const incomingGroupCallRef = useRef<IncomingGroupCall | null>(null);
  incomingGroupCallRef.current = incomingGroupCall;

  const userRef = useRef(user);
  userRef.current = user;

  const handleSignalOut = useCallback((targetUserId: string, signalType: 'OFFER' | 'ANSWER' | 'ICE_CANDIDATE', payload: any) => {
    if (!activeGroupRoomRef.current || !userRef.current) return;
    ws.send('/app/call.group.signal', {
      roomId: activeGroupRoomRef.current.roomId,
      channelId: activeGroupRoomRef.current.channelId,
      workspaceId: activeGroupRoomRef.current.workspaceId,
      targetUserId,
      signalType,
      callType: activeGroupRoomRef.current.callType,
      payload,
    });
  }, []);

  const {
    localStream,
    remoteStreams,
    screenStream,
    isMuted,
    isCameraOff,
    isScreenSharing,
    initLocalMedia,
    connectToPeer,
    handleRemoteSignal,
    toggleMute: toggleMuteHook,
    toggleCamera: toggleCameraHook,
    toggleScreenShare: toggleScreenShareHook,
    cleanupPeerConnection,
    cleanupAll: cleanupWebRTC,
  } = useGroupWebRTC({
    myUserId: user?.id || '',
    onSignal: handleSignalOut,
    iceServers,
  });

  const connectToPeerRef = useRef(connectToPeer);
  connectToPeerRef.current = connectToPeer;

  const handleRemoteSignalRef = useRef(handleRemoteSignal);
  handleRemoteSignalRef.current = handleRemoteSignal;

  const cleanupWebRTCRef = useRef(cleanupWebRTC);
  cleanupWebRTCRef.current = cleanupWebRTC;

  const leaveGroupCall = useCallback(() => {
    ringtonePlayer.stop();
    if (activeGroupRoomRef.current) {
      const isInitiating = activeGroupRoomRef.current.roomId === 'initiating';
      const isSoloCreator = activeGroupRoomRef.current.createdBy === userRef.current?.id && participants.length <= 1;

      const payload = {
        roomId: isInitiating ? undefined : activeGroupRoomRef.current.roomId,
        channelId: activeGroupRoomRef.current.channelId,
      };

      console.log('GROUP_CALL_CANCEL_SEND', {
        roomId: payload.roomId,
        channelId: payload.channelId,
        isInitiating,
        isSoloCreator,
        userId: userRef.current?.id,
      });

      if (isInitiating || isSoloCreator) {
        ws.send('/app/call.group.cancel', payload);
      } else {
        ws.send('/app/call.group.leave', payload);
      }
    }
    cleanupWebRTCRef.current();
    setActiveGroupRoom(null);
    setParticipants([]);
    setIsJoined(false);
  }, [participants.length]);

  const declineGroupCall = useCallback(() => {
    ringtonePlayer.stop();
    if (incomingGroupCallRef.current) {
      ws.send('/app/call.group.decline', {
        roomId: incomingGroupCallRef.current.room.roomId,
        channelId: incomingGroupCallRef.current.room.channelId,
      });
      setIncomingGroupCall(null);
    }
  }, []);

  const fetchActiveChannelCall = useCallback(async (workspaceId: string, channelId: string): Promise<GroupCallRoomDto | null> => {
    try {
      return await api.groupCalls.getActiveChannelCall(workspaceId, channelId);
    } catch (_err) {
      return null;
    }
  }, []);

  const joinGroupCall = useCallback(async (params: { workspaceId: string; channelId: string; callType: CallType }) => {
    if (!userRef.current) return;
    ringtonePlayer.stop();
    setRoomFullError(null);

    try {
      const servers = await api.groupCalls.getIceServers(params.workspaceId);
      if (servers && Array.isArray(servers)) {
        setIceServers(servers);
      }
    } catch (_e) {
      // Fallback STUN
    }

    try {
      await initLocalMedia(params.callType);
    } catch (err) {
      console.error('[GroupCall] Media access denied:', err);
      return;
    }

    ws.send('/app/call.group.join', {
      workspaceId: params.workspaceId,
      channelId: params.channelId,
      callType: params.callType,
    });
  }, [initLocalMedia]);

  const initiateGroupCall = useCallback(async (params: { workspaceId: string; channelId: string; callType: CallType }) => {
    if (!userRef.current) return;
    ringtonePlayer.playOutgoingChime();
    setRoomFullError(null);

    try {
      const servers = await api.groupCalls.getIceServers(params.workspaceId);
      if (servers && Array.isArray(servers)) {
        setIceServers(servers);
      }
    } catch (_e) {
      // Fallback STUN
    }

    try {
      await initLocalMedia(params.callType);
    } catch (err) {
      ringtonePlayer.stop();
      console.error('[GroupCall] Media access denied:', err);
      return;
    }

    // Optimistically show caller grid UI while outgoing chime plays and room initializes
    setActiveGroupRoom({
      roomId: 'initiating',
      channelId: params.channelId,
      workspaceId: params.workspaceId,
      callType: params.callType,
      createdBy: userRef.current.id,
      participants: [
        {
          userId: userRef.current.id,
          displayName: userRef.current.displayName,
          avatarUrl: userRef.current.avatarUrl || undefined,
          audioMuted: false,
          videoOff: false,
          screenSharing: false,
          joinedAt: new Date().toISOString(),
        },
      ],
      createdAt: new Date().toISOString(),
      startedAt: new Date().toISOString(),
      status: 'ACTIVE',
    });
    setIsJoined(true);

    ws.send('/app/call.group.initiate', {
      workspaceId: params.workspaceId,
      channelId: params.channelId,
      callType: params.callType,
    });
  }, [initLocalMedia]);

  const acceptIncomingGroupCall = useCallback(async () => {
    if (!incomingGroupCallRef.current) return;
    const { room, callType } = incomingGroupCallRef.current;
    setIncomingGroupCall(null);
    await joinGroupCall({
      workspaceId: room.workspaceId,
      channelId: room.channelId,
      callType,
    });
  }, [joinGroupCall]);

  const notifyMediaToggle = useCallback((audioMuted: boolean, videoOff: boolean, screenSharing: boolean) => {
    if (!activeGroupRoomRef.current) return;
    ws.send('/app/call.group.media-toggle', {
      roomId: activeGroupRoomRef.current.roomId,
      audioMuted,
      videoOff,
      screenSharing,
    });
  }, []);

  const toggleMute = useCallback(() => {
    toggleMuteHook();
    notifyMediaToggle(!isMuted, isCameraOff, isScreenSharing);
  }, [toggleMuteHook, notifyMediaToggle, isMuted, isCameraOff, isScreenSharing]);

  const toggleCamera = useCallback(() => {
    toggleCameraHook();
    notifyMediaToggle(isMuted, !isCameraOff, isScreenSharing);
  }, [toggleCameraHook, notifyMediaToggle, isMuted, isCameraOff, isScreenSharing]);

  const toggleScreenShare = useCallback(() => {
    toggleScreenShareHook();
    notifyMediaToggle(isMuted, isCameraOff, !isScreenSharing);
  }, [toggleScreenShareHook, notifyMediaToggle, isMuted, isCameraOff, isScreenSharing]);

  const clearRoomFullError = useCallback(() => {
    setRoomFullError(null);
  }, []);

  // STOMP User Queue Listener (/user/queue/calls)
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribeQueue = ws.subscribe(topics.calls(), async (signal: GroupCallSignalMessage) => {
      console.log('[GroupCall] STOMP queue signal received on /user/queue/calls:', signal.signalType, signal);
      if (signal.signalType === 'GROUP_CALL_INCOMING') {
        const room = signal.payload?.room;
        if (room && !isJoined) {
          console.log('[GroupCall] Incoming group call notification:', room.roomId);
          try {
            ringtonePlayer.playIncomingRing();
          } catch (_e) {
            // Ignore audio autoplay restrictions so modal rendering is never blocked
          }
          setIncomingGroupCall({
            room,
            callerName: signal.payload?.callerName || 'Team Member',
            channelName: signal.payload?.channelName || 'general',
            callType: signal.callType,
          });
        }
      } else if (signal.signalType === 'GROUP_CALL_CANCELLED' || signal.signalType === 'ROOM_CANCELLED' || signal.signalType === 'ROOM_ENDED') {
        console.log('GROUP_CALL_CANCEL_RECEIVED_CLIENT', {
          roomId: signal.roomId,
          channelId: signal.channelId,
          signalType: signal.signalType,
          currentIncomingRoomId: incomingGroupCallRef.current?.room.roomId,
          currentIncomingChannelId: incomingGroupCallRef.current?.room.channelId,
        });
        if (incomingGroupCallRef.current) {
          const currentRoomId = incomingGroupCallRef.current.room.roomId;
          const currentChannelId = incomingGroupCallRef.current.room.channelId;
          if (!signal.roomId || signal.roomId === currentRoomId || signal.channelId === currentChannelId) {
            console.log('GROUP_CALL_CANCEL_STATE_CLEARED', { roomId: currentRoomId });
            ringtonePlayer.stop();
            setIncomingGroupCall(null);
          }
        }
      } else if (signal.signalType === 'GROUP_CALL_ROOM_JOINED' || (signal.signalType === 'ROOM_JOINED' && signal.payload?.room?.roomId)) {
        ringtonePlayer.stop();
        const room = signal.payload?.room;
        if (room && room.roomId) {
          console.log('[GroupCall] Successfully joined group call room:', room.roomId);
          setActiveGroupRoom(room);
          setParticipants(room.participants || []);
          setIsJoined(true);
          setCallState('CONNECTED');

          if (localStream && room.participants) {
            for (const p of room.participants) {
              if (p.userId !== user.id) {
                await connectToPeerRef.current(p.userId, localStream);
              }
            }
          }
        }
      } else if (signal.signalType === 'GROUP_CALL_ROOM_FULL') {
        ringtonePlayer.stop();
        console.warn('[GroupCall] Room full rejection received');
        setRoomFullError('Call is full. Maximum 4 participants allowed.');
        cleanupWebRTCRef.current();
        setIsJoined(false);
        setCallState('IDLE');
        setActiveGroupRoom(null);
      } else if (signal.signalType === 'ERROR') {
        ringtonePlayer.stop();
        console.error('[GroupCall] Server error signal received:', signal.payload?.error);
        setRoomFullError(signal.payload?.error || 'Unable to start call');
        cleanupWebRTCRef.current();
        setIsJoined(false);
        setCallState('ERROR');
        setActiveGroupRoom(null);
      } else if (signal.signalType === 'OFFER' || signal.signalType === 'ANSWER' || signal.signalType === 'ICE_CANDIDATE') {
        if (signal.senderId) {
          await handleRemoteSignalRef.current(signal.senderId, signal.signalType, signal.payload);
        }
      }
    });

    return () => {
      unsubscribeQueue();
    };
  }, [user?.id, localStream, isJoined]);

  // STOMP Channel Topic Listener (/topic/workspace.{wId}.channel.{cId}.call)
  useEffect(() => {
    if (!activeGroupRoom?.workspaceId || !activeGroupRoom?.channelId) return;

    const channelCallTopic = topics.channelCall(activeGroupRoom.workspaceId, activeGroupRoom.channelId);
    const unsubscribeChannel = ws.subscribe(channelCallTopic, (signal: GroupCallSignalMessage) => {
      if (signal.signalType === 'PARTICIPANT_JOINED') {
        const newPart = signal.payload?.participant;
        if (newPart && newPart.userId !== userRef.current?.id) {
          console.log('[GroupCall] Participant joined:', newPart.userId);
          setParticipants((prev) => {
            if (prev.some((p) => p.userId === newPart.userId)) return prev;
            return [...prev, newPart];
          });
          if (localStream) {
            connectToPeerRef.current(newPart.userId, localStream);
          }
        }
      } else if (signal.signalType === 'PARTICIPANT_DECLINED') {
        const declinedUserId = signal.payload?.userId || signal.senderId;
        if (declinedUserId) {
          console.log('[GroupCall] Participant declined call:', declinedUserId);
          setParticipants((prev) =>
            prev.map((p) => (p.userId === declinedUserId ? { ...p, state: 'DECLINED' } : p))
          );
        }
      } else if (signal.signalType === 'PARTICIPANT_LEFT') {
        const leftUserId = (signal.payload as any)?.userId as string | undefined;
        if (leftUserId) {
          console.log('[GroupCall] Participant left:', leftUserId);
          setParticipants((prev) => prev.filter((p) => p.userId !== leftUserId));
          cleanupPeerConnection(leftUserId);
        }
      } else if (signal.signalType === 'PARTICIPANT_MEDIA_CHANGED') {
        const senderId = signal.senderId;
        const mediaState = signal.payload?.mediaState;
        if (senderId && mediaState) {
          setParticipants((prev) =>
            prev.map((p) =>
              p.userId === senderId
                ? {
                    ...p,
                    audioMuted: mediaState.audioMuted,
                    videoOff: mediaState.videoOff,
                    screenSharing: mediaState.screenSharing,
                  }
                : p
            )
          );
        }
      } else if (signal.signalType === 'ROOM_ENDED') {
        ringtonePlayer.stop();
        console.log('[GroupCall] Room ended by server');
        cleanupWebRTCRef.current();
        setActiveGroupRoom(null);
        setParticipants([]);
        setIsJoined(false);
      }
    });

    return () => {
      unsubscribeChannel();
    };
  }, [activeGroupRoom?.workspaceId, activeGroupRoom?.channelId, localStream, cleanupPeerConnection]);

  return (
    <GroupCallContext.Provider
      value={{
        activeGroupRoom,
        incomingGroupCall,
        participants,
        localStream,
        remoteStreams,
        screenStream,
        isJoined,
        isMuted,
        isCameraOff,
        isScreenSharing,
        callState,
        roomFullError,
        initiateGroupCall,
        joinGroupCall,
        acceptIncomingGroupCall,
        declineGroupCall,
        leaveGroupCall,
        toggleMute,
        toggleCamera,
        toggleScreenShare,
        clearRoomFullError,
        fetchActiveChannelCall,
      }}
    >
      {children}
    </GroupCallContext.Provider>
  );
};

export const useGroupCall = () => {
  const context = useContext(GroupCallContext);
  if (!context) {
    throw new Error('useGroupCall must be used within a GroupCallProvider');
  }
  return context;
};
